import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Button, Col, Form, Row, Table, Spinner,
  InputGroup, DropdownButton, Dropdown,
} from 'react-bootstrap';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import {
  ArrowLeft, FileEarmarkSpreadsheet, Search,
  CloudDownload, X, Clock, ArrowClockwise,
  CheckCircle, ExclamationTriangle, HourglassSplit,
  BoxArrowDown, XCircle,
} from 'react-bootstrap-icons';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import OutletSelect from '../../components/OutletSelect';

/* ─── helpers ─── */
const safeArray = (val) => (Array.isArray(val) ? val : []);
const fmt = (n) => `₹ ${Number(n || 0).toFixed(2)}`;

const isLongRange = (start, end) => {
  if (!start || !end) return false;
  return (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24) > 90;
};

/* ─────────────────────────────────────────────────────────────────────────────
   Download Manager CSS
───────────────────────────────────────────────────────────────────────────── */
const DM_CSS = `
  :root {
    --ar-bg:#f0f2f5; --ar-surface:#fff; --ar-border:#e2e6ea; --ar-text:#1a202c;
    --ar-muted:#41505F; --ar-accent:#2563eb; --ar-accent-light:#eff6ff; --ar-accent-dark:#1d4ed8;
    --ar-success:#059669; --ar-success-light:#ecfdf5; --ar-warning:#d97706; --ar-warning-light:#fffbeb;
    --ar-danger:#dc2626; --ar-danger-light:#fef2f2; --ar-shadow-lg:0 10px 40px rgba(0,0,0,.12);
    --ar-radius:10px; --ar-radius-sm:8px; --ar-radius-xs:6px;
  }

  .totals-row td {
    background-color: inherit !important;
  }

  .ar-dm-fab{
    height:38px; border-radius:var(--ar-radius-xs); padding:0 16px;
    background:#fff; border:1.5px solid var(--ar-border);
    display:inline-flex; align-items:center; justify-content:center;
    cursor:pointer; transition:all .18s;
    color:var(--ar-muted); font-size:13px; font-weight:600; gap:7px;
    white-space:nowrap; box-shadow:0 1px 3px rgba(0,0,0,.08);
  }
  .ar-dm-fab:hover{border-color:var(--ar-accent);color:var(--ar-accent);background:var(--ar-accent-light);}
  .ar-dm-fab.has-active{border-color:var(--ar-accent);color:var(--ar-accent);background:var(--ar-accent-light);}
  .ar-dm-badge{
    background:#dc2626; color:#fff;
    width:18px; height:18px; border-radius:50%;
    margin-left:2px;
    font-size:10px; font-weight:700;
    display:inline-flex; align-items:center; justify-content:center;
  }

  .ar-dm-panel{
    position:fixed; top:72px; right:24px; z-index:1049;
    width:580px; max-height:700px;
    background:var(--ar-surface);
    border-radius:14px;
    border:1.5px solid var(--ar-border);
    box-shadow:0 20px 60px rgba(0,0,0,.15),0 4px 16px rgba(0,0,0,.08);
    display:flex; flex-direction:column;
    overflow:hidden;
    animation:ar-dm-in .2s ease;
  }
  @keyframes ar-dm-in{from{opacity:0;transform:translateY(-8px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}

  .ar-dm-header{
    display:flex; align-items:flex-start; justify-content:space-between;
    padding:20px 22px 18px;
    background:linear-gradient(135deg,#0f172a 0%,#1e3a8a 60%,#1d4ed8 100%);
    color:#fff; flex-shrink:0;
  }
  .ar-dm-header-left{flex:1;}
  .ar-dm-header-title{display:flex;align-items:center;gap:9px;font-size:15px;font-weight:700;letter-spacing:-.2px;}
  .ar-dm-header-sub{font-size:11.5px;opacity:.6;margin-top:3px;}
  .ar-dm-stats{display:flex;gap:10px;margin-top:14px;}
  .ar-dm-stat{background:rgba(255,255,255,.1);border-radius:8px;padding:9px 16px;text-align:center;border:1px solid rgba(255,255,255,.12);min-width:70px;}
  .ar-dm-stat-val{font-size:20px;font-weight:700;color:#fff;line-height:1;}
  .ar-dm-stat-lbl{font-size:10px;opacity:.65;margin-top:4px;text-transform:uppercase;letter-spacing:.6px;}
  .ar-dm-header-actions{display:flex;align-items:center;gap:6px;flex-shrink:0;padding-top:2px;}
  .ar-dm-icon-btn{width:30px;height:30px;border-radius:7px;border:none;background:rgba(255,255,255,.12);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s;}
  .ar-dm-icon-btn:hover{background:rgba(255,255,255,.25);}

  .ar-dm-body{overflow-y:auto;flex:1;padding:14px 16px;display:flex;flex-direction:column;gap:10px;background:#f8fafc;}
  .ar-dm-body::-webkit-scrollbar{width:5px;}
  .ar-dm-body::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:4px;}

  .ar-dm-job{display:flex;align-items:center;gap:12px;padding:14px 16px;border-radius:10px;border:1px solid var(--ar-border);background:#fff;transition:box-shadow .15s,border-color .15s;}
  .ar-dm-job:hover{box-shadow:0 4px 12px rgba(0,0,0,.09);border-color:#cbd5e1;}
  .ar-dm-job-icon{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:17px;}
  .ar-dm-job-icon.pending{background:#fef3c7;color:#d97706;}
  .ar-dm-job-icon.processing{background:var(--ar-accent-light);color:var(--ar-accent);}
  .ar-dm-job-icon.completed{background:var(--ar-success-light);color:var(--ar-success);}
  .ar-dm-job-icon.failed{background:var(--ar-danger-light);color:var(--ar-danger);}
  .ar-dm-job-info{flex:1;min-width:0;}
  .ar-dm-job-name{font-size:13px;font-weight:600;color:var(--ar-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .ar-dm-job-meta{font-size:11.5px;color:#64748b;margin-top:3px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;}

  .ar-pb-wrap{height:4px;background:#e2e8f0;border-radius:3px;margin-top:7px;overflow:hidden;}
  .ar-pb{height:100%;border-radius:3px;transition:width .5s ease;}
  .ar-pb.pending{background:#fcd34d;width:8%;}
  .ar-pb.processing{background:var(--ar-accent);width:60%;animation:ar-pb-shimmer 1.5s ease-in-out infinite;}
  .ar-pb.completed{background:var(--ar-success);width:100%;}
  .ar-pb.failed{background:var(--ar-danger);width:100%;}
  @keyframes ar-pb-shimmer{0%,100%{opacity:1;}50%{opacity:.55;}}

  .ar-status-pill{font-size:10.5px;font-weight:700;padding:3px 10px;border-radius:20px;white-space:nowrap;flex-shrink:0;}
  .ar-status-pill.pending{background:#fef3c7;color:#d97706;}
  .ar-status-pill.processing{background:var(--ar-accent-light);color:var(--ar-accent);}
  .ar-status-pill.completed{background:var(--ar-success-light);color:var(--ar-success);}
  .ar-status-pill.failed{background:var(--ar-danger-light);color:var(--ar-danger);}

  .ar-dl-btn{height:32px;padding:0 14px;border-radius:var(--ar-radius-xs);border:1.5px solid #a7f3d0;background:var(--ar-success-light);color:var(--ar-success);font-size:12px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;transition:all .15s;white-space:nowrap;flex-shrink:0;}
  .ar-dl-btn:hover{background:var(--ar-success);color:#fff;border-color:var(--ar-success);}

  .ar-del-btn{width:30px;height:30px;border-radius:var(--ar-radius-xs);border:1.5px solid #fecaca;background:var(--ar-danger-light);color:var(--ar-danger);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:all .15s;flex-shrink:0;}
  .ar-del-btn:hover:not(:disabled){background:var(--ar-danger);color:#fff;border-color:var(--ar-danger);}
  .ar-del-btn:disabled{opacity:.5;cursor:not-allowed;}

  .ar-regen-btn{width:30px;height:30px;border-radius:var(--ar-radius-xs);border:1.5px solid #bfdbfe;background:var(--ar-accent-light);color:var(--ar-accent);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:all .15s;flex-shrink:0;}
  .ar-regen-btn:hover:not(:disabled){background:var(--ar-accent);color:#fff;border-color:var(--ar-accent);}
  .ar-regen-btn:disabled{opacity:.5;cursor:not-allowed;}

  .ar-dm-footer{padding:12px 18px;border-top:1px solid var(--ar-border);flex-shrink:0;display:flex;align-items:center;justify-content:space-between;background:#fff;}
  .ar-dm-footer-info{font-size:12px;color:var(--ar-muted);display:flex;align-items:center;gap:5px;}
  .ar-dm-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 20px;color:var(--ar-muted);text-align:center;gap:10px;}

  .ar-spin{animation:ar-spin .9s linear infinite;display:inline-block;}
  @keyframes ar-spin{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
  .ar-pulse{animation:ar-pulse 1.5s ease-in-out infinite;}
  @keyframes ar-pulse{0%,100%{opacity:1;}50%{opacity:.5;}}

  @media(max-width:768px){
    .ar-dm-panel{width:calc(100vw - 32px);left:16px;right:16px;top:72px;}
  }
`;

/* ─────────────────────────────────────────────────────────────────────────────
   SortIcon helper
───────────────────────────────────────────────────────────────────────────── */
const SortIcon = ({ sortConfig, colKey }) => {
  if (sortConfig.key !== colKey)
    return <span style={{ opacity: 0.3, marginLeft: 3, fontSize: 10 }}>⇅</span>;
  return (
    <span style={{ marginLeft: 3, fontSize: 10, color: '#0d6efd' }}>
      {sortConfig.direction === 'asc' ? '↑' : '↓'}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Shared table styles
───────────────────────────────────────────────────────────────────────────── */
const tdStyle = { verticalAlign: 'middle', padding: '8px 12px', borderColor: '#f1f5f9' };
const thStyle = {
  verticalAlign: 'middle', padding: '10px 12px',
  fontSize: 11, fontWeight: 700, letterSpacing: '0.6px',
  textTransform: 'uppercase', color: '#64748b',
  borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc',
  cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
};

const thBase = {
  verticalAlign: 'middle', padding: '10px 12px', fontSize: 11, fontWeight: 700,
  letterSpacing: '0.6px', textTransform: 'uppercase', color: '#64748b',
  borderBottom: '2px solid #e2e8f0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
};

const thColStyles = {
  sr:     { ...thBase, background: '#f1f5f9', textAlign: 'center', width: 44 },
  outlet: { ...thBase, background: '#fff7ed' },
  cat:    { ...thBase, background: '#eff6ff' },
  prod:   { ...thBase, background: '#f0fdf4' },
  addon:  { ...thBase, background: '#fdf4ff' },
  value:  { ...thBase, background: '#f9fafb' },
  count:  { ...thBase, background: '#eff6ff', textAlign: 'center' },
  rev:    { ...thBase, background: '#f0fdf4', textAlign: 'right' },
};

const colStyles = {
  sr:     { background: '#f8fafc', textAlign: 'center', fontWeight: 700, color: '#0d6efd', width: 44 },
  outlet: { background: '#fff8f0', color: '#92400e', fontWeight: 500, fontSize: 12 },
  cat:    { background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: 11, letterSpacing: '0.2px' },
  prod:   { background: '#f0fdf4', color: '#166534', fontWeight: 600, fontSize: 13 },
  addon:  { background: '#fdf4ff', color: '#7e22ce', fontWeight: 500, fontSize: 13 },
  value:  { background: '#ffffff', color: '#374151', fontSize: 12 },
  count:  { background: '#eff6ff', textAlign: 'center', fontWeight: 700, color: '#0d6efd', fontSize: 13 },
  rev:    { background: '#f0fdf4', textAlign: 'right', color: '#16a34a', fontWeight: 700, fontSize: 13 },
};

/* ─────────────────────────────────────────────────────────────────────────────
   Alternating row colors for different serial numbers
───────────────────────────────────────────────────────────────────────────── */
const ROW_COLOR_1 = '#ffffff'; // White for even serial numbers
const ROW_COLOR_2 = '#f8fafc'; // Light gray-blue for odd serial numbers

/* ─────────────────────────────────────────────────────────────────────────────
   VIEW MODES
───────────────────────────────────────────────────────────────────────────── */
const VIEW_CUMULATIVE = 'cumulative';
const VIEW_OUTLET     = 'outlet';
const VIEW_CATEGORY   = 'category';

/* ═══════════════════════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════════════════════ */
const AddonSalesReport = () => {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin]   = useState(currentSkin);
  const navigate          = useNavigate();
  const accessToken       = localStorage.getItem('authToken');
  const animatedComponents = makeAnimated();

  /* ── Filter state ── */
  const [selectedOutlets,      setSelectedOutlets]      = useState([]);
  const [categoryList,         setCategoryList]         = useState([]);
  const [selectedCategories,   setSelectedCategories]   = useState([]);
  const [addons,               setAddons]               = useState([]);
  const [addonGroup,           setAddonGroup]           = useState(null);
  const [filteredAddonValues,  setFilteredAddonValues]  = useState([]);
  const [selectedAddonValue,   setSelectedAddonValue]   = useState(null);
  const [startDate,            setStartDate]            = useState(null);
  const [endDate,              setEndDate]              = useState(null);
  const [error,                setError]                = useState('');

  /* ── Result state ── */
  const [rawData,              setRawData]              = useState([]);   // flat array from API
  const [loading,              setLoading]              = useState(false);
  const [btnLoading,           setBtnLoading]           = useState(false);
  const [downloadBtnLoading,   setDownloadBtnLoading]   = useState(false);
  const [clicked,              setClicked]              = useState(false);
  const [isMultiOutlet,        setIsMultiOutlet]        = useState(false);
  const [searchText,           setSearchText]           = useState('');
  const [sortConfig,           setSortConfig]           = useState({ key: null, direction: 'asc' });
  const [viewMode,             setViewMode]             = useState(VIEW_CATEGORY);
  const [activeOutletTab,      setActiveOutletTab]      = useState(0);
  const [categoryCollapsed,    setCategoryCollapsed]    = useState({});

  /* ── Download Manager state ── */
  const [dmOpen,           setDmOpen]           = useState(false);
  const [downloadJobs,     setDownloadJobs]     = useState([]);
  const [pollingActive,    setPollingActive]    = useState(false);
  const [jobsLoading,      setJobsLoading]      = useState(false);
  const [deletingJobId,    setDeletingJobId]    = useState(null);
  const [regeneratingJobId,setRegeneratingJobId]= useState(null);
  const [dmStartDate, setDmStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  });
  const [dmEndDate, setDmEndDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  });
  const pollingRef  = useRef(null);
  const dmPanelRef  = useRef(null);

  /* ═══════════════════════════════════════════════════════════
     DERIVED DATA
  ═══════════════════════════════════════════════════════════ */

  // Group flat rawData by outlet for outlet-wise view
  const outletGroups = useMemo(() => {
    const map = {};
    safeArray(rawData).forEach(row => {
      const key = row.outletId || row.outletName;
      if (!map[key]) map[key] = { outletId: row.outletId, outletName: row.outletName, rows: [] };
      map[key].rows.push(row);
    });
    return Object.values(map);
  }, [rawData]);

  // Group flat rawData by category for category-wise view
  const categoryGroups = useMemo(() => {
    const map = {};
    safeArray(rawData).forEach(row => {
      const key = row.categoryName || 'Uncategorized';
      if (!map[key]) map[key] = [];
      map[key].push(row);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [rawData]);

  // Filter by search
  const filteredData = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rawData;
    return safeArray(rawData).filter(r =>
      r.productName?.toLowerCase().includes(q) ||
      r.addonTitle?.toLowerCase().includes(q) ||
      r.selectedValue?.toLowerCase().includes(q)
    );
  }, [rawData, searchText]);

  // Filtered outlet groups
  const filteredOutletGroups = useMemo(() => {
    const map = {};
    safeArray(filteredData).forEach(row => {
      const key = row.outletId || row.outletName;
      if (!map[key]) map[key] = { outletId: row.outletId, outletName: row.outletName, rows: [] };
      map[key].rows.push(row);
    });
    return Object.values(map);
  }, [filteredData]);

  // Filtered category groups
  const filteredCategoryGroups = useMemo(() => {
    const map = {};
    safeArray(filteredData).forEach(row => {
      const key = row.categoryName || 'Uncategorized';
      if (!map[key]) map[key] = [];
      map[key].push(row);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredData]);

  const hasAnyData = safeArray(rawData).length > 0;

  /* ─────────────────────────────────────────────────────────────────────────
     GRAND TOTALS
     IMPORTANT: The API returns repeated order-level financials per addon-value
     row (all values in a productId+addonId group share the same totalGrandTotal
     etc.). We must sum financials ONCE per (productId+addonId) group to avoid
     double-counting. totalSaleCount is per-value so sum across all rows.
  ───────────────────────────────────────────────────────────────────────── */
  const calcTotals = (rows) => {
    const seen = new Set();
    let count = 0, taxable = 0;
    safeArray(rows).forEach(r => {
      count += (r.totalSaleCount || 0);
      const gKey = `${r.productId}__${r.addonId}`;
      if (!seen.has(gKey)) {
        seen.add(gKey);
        taxable  += (r.totalTaxable    || 0);
      }
    });
    return { count, taxable };
  };

  const grandTotals = useMemo(() => calcTotals(filteredData), [filteredData]);

  /* ── Sort ── */
  const handleSort = (key) =>
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

  /* ─────────────────────────────────────────────────────────────────────────
     sortRows — sorts the flat array.
     For group-level keys (financials, productName, addonTitle) we sort by
     the first row of each (productId+addonId) group so the group stays intact.
     For row-level keys (selectedValue, totalSaleCount) we sort within groups.
  ───────────────────────────────────────────────────────────────────────── */
  const sortRows = (rows) => {
    if (!sortConfig.key) return safeArray(rows);

    const GROUP_LEVEL_KEYS = [
      'productName', 'addonTitle', 'categoryName', 'outletName',
      'totalTaxable',
    ];

    if (GROUP_LEVEL_KEYS.includes(sortConfig.key)) {
      // Build segments, sort segments, then flatten
      const segments = buildSegments(safeArray(rows));
      segments.sort((a, b) => {
        const av = a.rows[0][sortConfig.key] ?? 0;
        const bv = b.rows[0][sortConfig.key] ?? 0;
        if (typeof av === 'string')
          return sortConfig.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        return sortConfig.direction === 'asc' ? av - bv : bv - av;
      });
      return segments.flatMap(s => s.rows);
    }

    // Row-level sort (selectedValue, totalSaleCount) — sort within each segment
    const segments = buildSegments(safeArray(rows));
    return segments.flatMap(s => {
      const sorted = [...s.rows].sort((a, b) => {
        const av = a[sortConfig.key] ?? 0;
        const bv = b[sortConfig.key] ?? 0;
        if (typeof av === 'string')
          return sortConfig.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        return sortConfig.direction === 'asc' ? av - bv : bv - av;
      });
      return sorted;
    });
  };

  /* ─────────────────────────────────────────────────────────────────────────
     buildSegments — groups consecutive rows by (productId + addonId)
  ───────────────────────────────────────────────────────────────────────── */
  const buildSegments = (rows) => {
    const segments = [];
    let seg = null;
    rows.forEach((row) => {
      const key = `${row.productId}__${row.addonId}`;
      if (!seg || seg.key !== key) {
        seg = { key, rows: [row] };
        segments.push(seg);
      } else {
        seg.rows.push(row);
      }
    });
    return segments;
  };

  /* ═══════════════════════════════════════════════════════════
     DOWNLOAD MANAGER — POLLING
  ═══════════════════════════════════════════════════════════ */
  const toDateStr = (d) => {
    if (!d) { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`; }
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  const fetchDownloadStatus = useCallback(async (silent = false, overrideStart, overrideEnd) => {
    try {
      if (!silent) setJobsLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const sDate = overrideStart !== undefined ? overrideStart : dmStartDate;
      const eDate = overrideEnd   !== undefined ? overrideEnd   : dmEndDate;
      const res = await axios.get('report/get-report-status/ADDON_SALES_REPORT', {
        headers,
        params: { startDate: toDateStr(sDate), endDate: toDateStr(eDate) },
      });
      const jobs = res?.data?.data?.reportRequests || res?.data?.reportRequests || [];
      const jobArr = Array.isArray(jobs) ? jobs : [];
      const sorted = [...jobArr].sort((a, b) => {
        const tA = a.status === 'COMPLETED' || a.status === 'FAILED';
        const tB = b.status === 'COMPLETED' || b.status === 'FAILED';
        if (tA !== tB) return tA ? 1 : -1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      setDownloadJobs(sorted);
      if (jobArr.length > 0 && jobArr.every(j => j.status === 'COMPLETED' || j.status === 'FAILED'))
        stopPolling();
    } catch (e) {
      console.error('Poll error:', e);
    } finally {
      if (!silent) setJobsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, dmStartDate, dmEndDate]);

  const startPolling = useCallback(() => {
    setPollingActive(true);
    fetchDownloadStatus(true);
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => fetchDownloadStatus(true), 5000);
  }, [fetchDownloadStatus]);

  const stopPolling = useCallback(() => {
    setPollingActive(false);
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  }, []);

  /* ── Job status helpers ── */
  const getJobCls = (s) => {
    const v = s?.toLowerCase();
    if (v === 'completed') return 'completed';
    if (v === 'failed')    return 'failed';
    if (v === 'no_data')   return 'no_data';
    if (v === 'processing' || v === 'in-progress') return 'processing';
    return 'pending';
  };
  const getJobIcon = (s) => {
    const c = getJobCls(s);
    if (c === 'completed') return <CheckCircle size={15} />;
    if (c === 'failed')    return <ExclamationTriangle size={15} />;
    if (c === 'processing') return <span className="ar-spin"><ArrowClockwise size={15} /></span>;
    return <HourglassSplit size={15} />;
  };
  const getJobLabel = (s) => ({
    completed: 'Completed', failed: 'Failed',
    no_data: 'No data found', processing: 'Processing…', pending: 'Queued',
  }[getJobCls(s)] || 'Queued');

  const pendingCount   = downloadJobs.filter(j => ['pending','processing'].includes(getJobCls(j.status))).length;
  const completedCount = downloadJobs.filter(j => getJobCls(j.status) === 'completed').length;

  const downloadFile = (job) => {
    const url = job.storagePath;
    if (!url) { toast.error('File URL not available.'); return; }
    const link = document.createElement('a');
    link.href = url;
    link.download = `Addon_Sales_Report_${job.queryParams?.startDate}_${job.queryParams?.endDate}.xlsx`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const handleDeleteReportRequest = async (jobId) => {
    try {
      setDeletingJobId(jobId);
      await axios.delete('report/delete-report-request', {
        headers: { Authorization: `Bearer ${accessToken}` },
        data: { requestId: jobId },
      });
      toast.success('Request cancelled successfully.', { position: 'top-right', autoClose: 2000, theme: 'light' });
      fetchDownloadStatus(true);
    } catch {
      toast.error('Failed to cancel request.', { position: 'top-right', autoClose: 3000, theme: 'light' });
    } finally {
      setDeletingJobId(null);
    }
  };

  const handleRegenerateReport = async (jobId) => {
    try {
      setRegeneratingJobId(jobId);
      await axios.delete('report/delete-report-storage-path', {
        headers: { Authorization: `Bearer ${accessToken}` },
        data: { requestId: jobId },
      });
      toast.success('Report regeneration triggered! Please wait…', { position: 'top-right', autoClose: 2500, theme: 'light' });
      startPolling();
      fetchDownloadStatus(true);
    } catch {
      toast.error('Failed to trigger regeneration.', { position: 'top-right', autoClose: 3000, theme: 'light' });
    } finally {
      setRegeneratingJobId(null);
    }
  };

  const handleDmDateChange = (type, date) => {
    if (type === 'start') {
      setDmStartDate(date);
      if (date && dmEndDate) {
        const diff = (dmEndDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
        if (diff > 30) {
          const clamped = new Date(date); clamped.setDate(clamped.getDate() + 30);
          setDmEndDate(clamped);
          fetchDownloadStatus(false, date, clamped);
          return;
        }
      }
      fetchDownloadStatus(false, date, dmEndDate);
    } else {
      setDmEndDate(date);
      fetchDownloadStatus(false, dmStartDate, date);
    }
  };

  /* ── DM trigger for multi-outlet ── */
  const triggerMultiOutletDownload = async () => {
    try {
      setDownloadBtnLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const qs = new URLSearchParams();
      if (startDate) qs.append('startDate', startDate.toISOString().split('T')[0]);
      if (endDate)   qs.append('endDate',   endDate.toISOString().split('T')[0]);
      if (addonGroup)        qs.append('addonId',      addonGroup.value);
      if (selectedAddonValue) qs.append('addonValueId', selectedAddonValue.value);
      qs.append('isDownload', 'true');

      await axios.post(
        `addons/addon-sales-report-for-outlet/new?${qs.toString()}`,
        {
          outletIds:   selectedOutlets.map(o => o.value),
          categoryIds: selectedCategories.map(c => c.value),
        },
        { headers }
      );

      toast.success('Download job queued! Check the Downloads panel.', {
        position: 'top-right', autoClose: 2500, theme: 'light',
      });
      setDmOpen(true);
      startPolling();
    } catch (err) {
      const s = err?.response?.status;
      const msg = s === 406
        ? err.response?.data?.error?.message || err.response?.data?.error
        : 'Failed to queue download job.';
      toast.error(msg, { position: 'top-right', autoClose: 3000, theme: 'light' });
    } finally {
      setDownloadBtnLoading(false);
    }
  };

  /* ─── Init ─── */
  useEffect(() => {
    const now   = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    setStartDate(new Date(first.setHours(23, 59, 59, 999)));
    setEndDate(new Date(today.setHours(23, 59, 59, 999)));
    getCategoryList();
    getAddons();
    fetchDownloadStatus(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dmPanelRef.current && !dmPanelRef.current.contains(e.target)) {
        const fab = document.getElementById('ar-dm-fab');
        if (fab && !fab.contains(e.target)) setDmOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // Addon values when group changes
  useEffect(() => {
    if (addonGroup) {
      const grp = addons.find(a => a._id === addonGroup.value);
      setFilteredAddonValues((grp?.addonValues ?? []).map(v => ({ value: v._id, label: v.value })));
    } else {
      setFilteredAddonValues([]);
      setSelectedAddonValue(null);
    }
  }, [addonGroup, addons]);

  /* ─── API ─── */
  const getCategoryList = async () => {
    try {
      const res = await axios.get('masters/category/get', { headers: { Authorization: `Bearer ${accessToken}` } });
      if (res?.data?.status) setCategoryList(res?.data?.data?.categories ?? []);
    } catch (e) { handleApiError(e); }
  };

  const getAddons = async () => {
    try {
      const res = await axios.get('addons?page=1&limit=1000', { headers: { Authorization: `Bearer ${accessToken}` } });
      setAddons(res?.data?.data?.addons ?? []);
    } catch { /* silent */ }
  };

  const fetchReport = async (sd, ed) => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const qs = new URLSearchParams();
      qs.append('startDate', sd.toISOString().split('T')[0]);
      qs.append('endDate',   ed.toISOString().split('T')[0]);
      if (addonGroup)        qs.append('addonId',      addonGroup.value);
      if (selectedAddonValue) qs.append('addonValueId', selectedAddonValue.value);

      const res = await axios.post(
        `addons/addon-sales-report-for-outlet/new?${qs.toString()}`,
        {
          outletIds:   selectedOutlets.map(o => o.value),
          categoryIds: selectedCategories.map(c => c.value),
        },
        { headers }
      );

      // API returns a flat array in res.data.data
      const flat = Array.isArray(res?.data?.data) ? res.data.data : [];
      setRawData(flat);
      setActiveOutletTab(0);
      setCategoryCollapsed({});
      toast.success('Data fetched successfully', { position: 'top-right', autoClose: 3000, theme: 'light' });
    } catch (e) {
      setRawData([]);
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async (isCosting) => {
    setDownloadBtnLoading(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const sd = new Date(new Date(startDate).setHours(23, 59, 59, 999));
      const ed = new Date(new Date(endDate).setHours(23, 59, 59, 999));
      const qs = new URLSearchParams();
      qs.append('startDate',  sd.toISOString().split('T')[0]);
      qs.append('endDate',    ed.toISOString().split('T')[0]);
      if (addonGroup)        qs.append('addonId',      addonGroup.value);
      if (selectedAddonValue) qs.append('addonValueId', selectedAddonValue.value);
      if (isCosting)         qs.append('isCosting',    'true');
      qs.append('isDownload', 'true');

      const res = await axios.post(
        `addons/addon-sales-report-for-outlet/new?${qs.toString()}`,
        {
          outletIds:   selectedOutlets.map(o => o.value),
          categoryIds: selectedCategories.map(c => c.value),
        },
        { headers, responseType: 'blob' }
      );

      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Addon Sales Report (${sd.toISOString().split('T')[0]} to ${ed.toISOString().split('T')[0]}).xlsx`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      toast.success('Excel downloaded successfully!', { position: 'top-right', autoClose: 3000, theme: 'light' });
    } catch (e) {
      handleApiError(e);
    } finally {
      setDownloadBtnLoading(false);
    }
  };

  const handleApiError = (e) => {
    let msg = 'An error occurred';
    const s = e.response?.status;
    if (s === 406) msg = e.response?.data?.error?.message || e.response?.data?.error;
    else if (s === 412) msg = e.response?.data?.data?.message;
    else if (s === 500) msg = e.response?.data?.error;
    toast.error(msg, { position: 'top-right', autoClose: 3000, theme: 'light' });
  };

  /* ─── Form submit ─── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setClicked(true);
    setBtnLoading(true);
    setSearchText('');
    const sd = new Date(new Date(startDate).setHours(23, 59, 59, 999));
    const ed = new Date(new Date(endDate).setHours(23, 59, 59, 999));
    const multi = selectedOutlets.length > 1 || isLongRange(startDate, endDate);
    setIsMultiOutlet(multi);
    if (multi) {
      await triggerMultiOutletDownload();
      setRawData([]);
    } else {
      await fetchReport(sd, ed);
    }
    setBtnLoading(false);
  };

  const handleEndDateChange = (date) => {
    if (startDate && new Date(date) < new Date(startDate)) {
      setError('End date cannot be earlier than start date');
    } else {
      setError(''); setEndDate(date);
    }
  };

  const toggleCategoryCollapse = (key) =>
    setCategoryCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  /* ─── Options ─── */
  const categoryOptions = categoryList.map(c => ({ value: c._id, label: c.name }));

  const selectStyles = {
    control: (p, s) => ({
      ...p, borderRadius: 8, fontSize: 13,
      borderColor: s.isFocused ? '#0d6efd' : '#ced4da',
      boxShadow: s.isFocused ? '0 0 0 3px rgba(13,110,253,.15)' : 'none',
      '&:hover': { borderColor: '#0d6efd' },
    }),
    multiValue: (p) => ({ ...p, backgroundColor: '#e7f0ff', borderRadius: 6 }),
    multiValueLabel: (p) => ({ ...p, color: '#0d6efd', fontWeight: 600 }),
    multiValueRemove: (p) => ({ ...p, color: '#0d6efd', ':hover': { backgroundColor: '#0d6efd', color: 'white' } }),
    placeholder: (p) => ({ ...p, color: '#9ca3af', fontSize: 13 }),
    option: (p, s) => ({ ...p, fontSize: 13, backgroundColor: s.isSelected ? '#0d6efd' : s.isFocused ? '#e7f0ff' : 'white' }),
  };

  const isMultiSelected = selectedOutlets.length > 1;

  /* ═══════════════════════════════════════════════════════════
     TABLE PARTS
  ═══════════════════════════════════════════════════════════ */
  const ThSortable = ({ colKey, align = 'left', children }) => (
    <th style={{ ...thStyle, textAlign: align }} onClick={() => handleSort(colKey)}>
      {children}<SortIcon sortConfig={sortConfig} colKey={colKey} />
    </th>
  );

  /* ─────────────────────────────────────────────────────────
     TableHead — with Outlet column
     Columns (8): #, Outlet, Category, Product, Addon Group,
                   Addon Value, Sales Count, Revenue
  ───────────────────────────────────────────────────────── */
  const TableHead = () => (
  <thead>
    <tr>
      <th style={thColStyles.sr} onClick={() => handleSort('sr')}>#</th>
      <th style={thColStyles.outlet} onClick={() => handleSort('outletName')}>Outlet <SortIcon sortConfig={sortConfig} colKey="outletName" /></th>
      <th style={thColStyles.cat} onClick={() => handleSort('categoryName')}>Category <SortIcon sortConfig={sortConfig} colKey="categoryName" /></th>
      <th style={thColStyles.prod} onClick={() => handleSort('productName')}>Product <SortIcon sortConfig={sortConfig} colKey="productName" /></th>
      <th style={thColStyles.addon} onClick={() => handleSort('addonTitle')}>Addon Group <SortIcon sortConfig={sortConfig} colKey="addonTitle" /></th>
      <th style={thColStyles.value}>Addon Value</th>
      <th style={thColStyles.count} onClick={() => handleSort('totalSaleCount')}>Sales Count <SortIcon sortConfig={sortConfig} colKey="totalSaleCount" /></th>
      <th style={thColStyles.rev} onClick={() => handleSort('totalTaxable')}>Revenue <SortIcon sortConfig={sortConfig} colKey="totalTaxable" /></th>
    </tr>
  </thead>
);

  /* ─────────────────────────────────────────────────────────
     TableHeadNoOutlet — for outlet-wise tabbed view
     Columns (7): #, Category, Product, Addon Group,
                   Addon Value, Sales Count, Revenue
  ───────────────────────────────────────────────────────── */
  const TableHeadNoOutlet = () => (
    <thead>
      <tr>
        <th style={{ ...thStyle, width: 44, textAlign: 'center' }}>#</th>
        <ThSortable colKey="categoryName">Category</ThSortable>
        <ThSortable colKey="productName">Product</ThSortable>
        <ThSortable colKey="addonTitle">Addon Group</ThSortable>
        <th style={thStyle}>Addon Value</th>
        <ThSortable colKey="totalSaleCount" align="center">Sales Count</ThSortable>
        <ThSortable colKey="totalTaxable" align="right">Revenue</ThSortable>
      </tr>
    </thead>
  );

  /* ═══════════════════════════════════════════════════════════
     buildGroupedRows
     ─────────────────────────────────────────────────────────
     Renders the flat API array as a grouped table matching
     the Excel layout:

     • Each API record  →  one <tr>  (one addon value per row)
     • Groups by (productId + addonId)
     • rowspan on: #, Outlet*, Category, Product, Addon Group, Revenue
       (* Outlet rowspan spans the whole product block)
     • Sr.No increments per unique productId
     • Thin separator row between every (productId+addonId) group
     • Financial col rowspan the group (same value repeats in API)
     • Alternating background colors for different product groups

     showOutlet: true  → include Outlet cell (combined / category views)
                 false → omit Outlet cell (outlet-wise tab)
  ═══════════════════════════════════════════════════════════ */
  const buildGroupedRows = (rows, showOutlet = true) => {
    if (!rows || rows.length === 0) return null;

    const segments = buildSegments(rows);

    const productSrMap = {};
    let srCounter = 0;
    segments.forEach(({ rows: sRows }) => {
      const pid = sRows[0].productId;
      if (!(pid in productSrMap)) {
        productSrMap[pid] = ++srCounter;
      }
    });

    const productColorMap = {};
    Object.entries(productSrMap).forEach(([pid, sr]) => {
      productColorMap[pid] = sr % 2 === 0 ? ROW_COLOR_2 : ROW_COLOR_1;
    });

    const productDataRows  = {};
    const productSegCount  = {};
    segments.forEach(({ rows: sRows }) => {
      const pid = sRows[0].productId;
      productDataRows[pid]  = (productDataRows[pid]  || 0) + sRows.length;
      productSegCount[pid]  = (productSegCount[pid]  || 0) + 1;
    });
    const productRowSpan = {};
    Object.keys(productDataRows).forEach(pid => {
      productRowSpan[pid] = productDataRows[pid] + productSegCount[pid];
    });

    const productSrRendered     = {};
    const productOutletRendered = {};

    const result = [];

    segments.forEach(({ key, rows: sRows }, segIdx) => {
      const firstRow           = sRows[0];
      const pid                = firstRow.productId;
      const sr                 = productSrMap[pid];
      const groupRowCount      = sRows.length;
      const groupRowSpan       = groupRowCount + 1;
      const fullProductRowSpan = productRowSpan[pid];

      const showSrNo       = !productSrRendered[pid];
      const showOutletCell = showOutlet && !productOutletRendered[pid];

      if (showSrNo)        productSrRendered[pid]     = true;
      if (showOutletCell)  productOutletRendered[pid] = true;

      sRows.forEach((row, rowIdx) => {
        const isFirstInGroup = rowIdx === 0;

        result.push(
          <tr key={`${key}-${rowIdx}`}>

            {/* ── Sr.No ── */}
            {showSrNo && isFirstInGroup && (
              <td rowSpan={fullProductRowSpan}
                style={{ ...tdStyle, ...colStyles.sr, verticalAlign: 'middle', borderRight: '1px solid #e2e8f0' }}>
                {sr}
              </td>
            )}

            {/* ── Outlet ── */}
            {showOutletCell && isFirstInGroup && (
              <td rowSpan={fullProductRowSpan}
                style={{ ...tdStyle, ...colStyles.outlet, verticalAlign: 'middle', whiteSpace: 'nowrap', borderRight: '1px solid #ffe4c4' }}>
                {row.outletName || '—'}
              </td>
            )}

            {/* ── Category ── */}
            {isFirstInGroup && (
              <td rowSpan={groupRowSpan}
                style={{ ...tdStyle, ...colStyles.cat, verticalAlign: 'middle', borderRight: '1px solid #dbeafe' }}>
                {row.categoryName || '—'}
              </td>
            )}

            {/* ── Product ── */}
            {isFirstInGroup && (
              <td rowSpan={groupRowSpan}
                style={{ ...tdStyle, ...colStyles.prod, verticalAlign: 'middle', borderRight: '1px solid #bbf7d0' }}>
                {row.productName || '—'}
              </td>
            )}

            {/* ── Addon Group ── */}
            {isFirstInGroup && (
              <td rowSpan={groupRowSpan}
                style={{ ...tdStyle, ...colStyles.addon, verticalAlign: 'middle', borderRight: '1px solid #e9d5ff' }}>
                {row.addonTitle || '—'}
              </td>
            )}

            {/* ── Addon Value ── */}
            <td style={{ ...tdStyle, ...colStyles.value }}>
              {row.selectedValue || '—'}
            </td>

            {/* ── Sales Count ── */}
            <td style={{ ...tdStyle, ...colStyles.count }}>
              {row.totalSaleCount ?? 0}
            </td>

            {/* ── Revenue ── */}
            {isFirstInGroup && (
              <td rowSpan={groupRowSpan}
                style={{ ...tdStyle, ...colStyles.rev, verticalAlign: 'middle' }}>
                {fmt(row.totalTaxable)}
              </td>
            )}

          </tr>
        );
      });

      /* ── separator row ── */
      const sepStyle = { padding: 0, height: 6, background: '#f1f5f9', borderTop: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef' };
      result.push(
        <tr key={`sep-${segIdx}`}>
          <td colSpan={2} style={sepStyle} />
        </tr>
      );
    });

    return result;
  };

  /* ─────────────────────────────────────────────────────────
     TotalsRow
     With outlet    (8 cols): span=6 then count, then revenue
     Without outlet (7 cols): span=5 then count, then revenue
  ───────────────────────────────────────────────────────── */
  const TotalsRow = ({ label, totals, withOutlet = true, bgColor = '#1e3a5f' }) => (
    <tr className="totals-row" style={{ backgroundColor: bgColor }}>
      <td
        colSpan={withOutlet ? 6 : 5}
        style={{
          ...tdStyle,
          textAlign: 'right',
          color: 'white',
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          backgroundColor: 'transparent',
        }}
      >
        {label}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center', color: 'white', fontWeight: 700, backgroundColor: 'transparent' }}>
        {totals.count}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', color: '#86efac', fontWeight: 700, backgroundColor: 'transparent' }}>
        {fmt(totals.taxable)}
      </td>
    </tr>
  );

  /* ─────────────────────────────────────────────────────────
     CategoryHeaderRow — collapsible category section header
  ───────────────────────────────────────────────────────── */
  const CategoryHeaderRow = ({ catName, rows, collapseKey, isCollapsed, showOutlet = true }) => {
    const t = calcTotals(rows);
    return (
      <tr
        onClick={() => toggleCategoryCollapse(collapseKey)}
        style={{ backgroundColor: '#eef4ff', cursor: 'pointer', userSelect: 'none' }}
      >
        <td colSpan={showOutlet ? 6 : 5} style={{ ...tdStyle, padding: '9px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: 11, color: '#0d6efd', transition: 'transform 0.15s',
              display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            }}>▼</span>
            <span style={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.4px', color: '#0d6efd', textTransform: 'uppercase' }}>
              {catName}
            </span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 20, height: 18, padding: '0 6px', borderRadius: 20,
              fontSize: 9, fontWeight: 700, backgroundColor: '#0d6efd', color: 'white',
            }}>
              {rows.length}
            </span>
          </div>
        </td>
        <td style={{ ...tdStyle, textAlign: 'center', color: '#0d6efd', fontSize: 12, fontWeight: 700 }}>
          {t.count}
        </td>
        <td style={{ ...tdStyle, textAlign: 'right', color: '#16a34a', fontSize: 12, fontWeight: 700 }}>
          {fmt(t.taxable)}
        </td>
      </tr>
    );
  };

  /* ═══════════════════════════════════════════════════════════
     STAT CHIPS
  ═══════════════════════════════════════════════════════════ */
  const StatChips = () => (
    <div className="d-flex gap-2 flex-wrap pt-1">
      <span className="asr-stat-chip">
        <i className="bi bi-sliders"></i>{outletGroups.length} Outlet{outletGroups.length !== 1 ? 's' : ''}
      </span>
      <span className="asr-stat-chip">
        <i className="bi bi-bag-check"></i>{grandTotals.count} Total Sales
      </span>
      <span className="asr-stat-chip" style={{ background: '#dcfce7', color: '#16a34a' }}>
        <i className="bi bi-currency-rupee"></i>{fmt(grandTotals.taxable)} Revenue
      </span>
    </div>
  );

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <React.Fragment>
      <style>{DM_CSS}</style>
      <style>{`
        .asr-filter-label {
          font-size: 12px;
          font-weight: 600;
          color: #475569;
          margin-bottom: 4px;
          display: block;
        }
        .asr-filter-box {
          background: #f8fafc;
          border: 1px solid #e9ecef;
          border-radius: 10px;
          padding: 12px 14px;
        }
        .asr-table tbody tr:hover td { background-color:#f0f7ff !important; transition:background 0.1s; }
        .asr-stat-chip { display:inline-flex;align-items:center;gap:6px;background:#e7f0ff;border-radius:8px;padding:4px 12px;font-size:12px;color:#0d6efd;font-weight:600; }
        .asr-view-btn { border:1.5px solid #e2e8f0; border-radius:8px; padding:6px 16px; font-size:13px; font-weight:600; cursor:pointer; transition:all 0.15s; background:white; color:#64748b; }
        .asr-view-btn.active { background:#0d6efd; color:white; border-color:#0d6efd; box-shadow:0 2px 8px rgba(13,110,253,.25); }
        .asr-multi-notice{margin:12px 0;padding:14px 18px;border-radius:8px;background:#fffbeb;border:1.5px solid #fde68a;display:flex;align-items:flex-start;gap:12px;}
        .asr-multi-notice-text{font-size:13px;color:#92400e;line-height:1.6;}
        .asr-multi-notice-text strong{color:#78350f;}

        /* Outlet tabs */
        .asr-tabs-bar{display:flex;gap:0;flex-wrap:wrap;background:#f8fafc;border:1px solid #e2e8f0;border-bottom:none;border-radius:12px 12px 0 0;padding:8px 8px 0 8px;overflow-x:auto;}
        .asr-tab-btn{display:inline-flex;align-items:center;gap:7px;padding:9px 16px 10px 16px;border:1px solid transparent;border-bottom:none;border-radius:8px 8px 0 0;background:transparent;color:#64748b;font-size:13px;font-weight:500;cursor:pointer;transition:all 0.15s;white-space:nowrap;position:relative;margin-bottom:-1px;}
        .asr-tab-btn:hover:not(.active){background:#eef4ff;color:#0d6efd;}
        .asr-tab-btn.active{background:white;color:#0d6efd;font-weight:700;border-color:#e2e8f0;border-bottom-color:white;box-shadow:0 -2px 0 0 #0d6efd inset;}
        .asr-tab-badge{display:inline-flex;align-items:center;justify-content:center;min-width:22px;height:18px;padding:0 6px;border-radius:20px;font-size:10px;font-weight:700;background:#e2e8f0;color:#64748b;transition:all 0.15s;}
        .asr-tab-btn.active .asr-tab-badge{background:#0d6efd;color:white;}
        .asr-tab-panel{border:1px solid #e2e8f0;border-radius:0 0 12px 12px;overflow:hidden;}
        .asr-outlet-card-header{background:linear-gradient(90deg,#eef4ff,#f8fafc);padding:10px 16px;border-bottom:1px solid #dbeafe;display:flex;align-items:center;gap:12px;}

        /* prevent hover bleed on separator rows */
        .asr-table tbody tr.asr-sep:hover td { background-color: #f1f5f9 !important; }
      `}</style>

      <Header onSkin={setSkin} />

      <div className={`main main-app p-3 p-lg-4 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>
        <div className="card border-0" style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,.06),0 4px 16px rgba(0,0,0,.04)', border: '1px solid #e2e8f0' }}>

          {/* ── Page Header ── */}
          <div className="card-header bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center" style={{ borderRadius: '16px 16px 0 0' }}>
            <div className="d-flex align-items-center gap-3">
              <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg,#0d6efd,#6ea8fe)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="bi bi-sliders text-white fs-5"></i>
              </div>
              <div>
                <h5 className="mb-0 fw-bold" style={{ color: '#1e293b', letterSpacing: '-0.3px' }}>ADDON SALES REPORT</h5>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Analyze addon performance across outlets</div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              {/* Download Manager FAB */}
              <button
                id="ar-dm-fab"
                className={`ar-dm-fab${pendingCount > 0 ? ' has-active' : ''}`}
                onClick={() => setDmOpen(o => !o)}
                title="Download Manager"
              >
                <CloudDownload size={15} />
                Downloads
                {pendingCount > 0 && <span className="ar-dm-badge ar-pulse">{pendingCount}</span>}
                {pendingCount === 0 && completedCount > 0 && (
                  <span className="ar-dm-badge" style={{ background: '#059669' }}>{completedCount}</span>
                )}
              </button>
              <Button variant="outline-secondary" size="sm" onClick={() => navigate('/reports/all-reports')}
                className="d-flex align-items-center fw-semibold" style={{ borderRadius: 4, height: 36 }}>
                <ArrowLeft className="me-1" size={14} /> Back to Reports
              </Button>
            </div>
          </div>
          <div style={{ height: 1, background: '#e2e8f0' }} />

          <div className="card-body p-4">
            <Form onSubmit={handleSubmit}>
              {/* ── Filter Row 1 ── */}
              <div className="asr-filter-box mb-3">
                <Row className="g-3">
                  <Col md={3}>
                    <OutletSelect
                      value={selectedOutlets}
                      onChange={(opts) => setSelectedOutlets(safeArray(opts))}
                      isMulti={true}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="fw-semibold" style={{ fontSize: 14, color: '#475569' }}>
                      <i className="bi bi-tags me-1"></i>Category
                    </Form.Label>
                    <Select
                      options={categoryOptions} value={selectedCategories}
                      onChange={(opts) => setSelectedCategories(safeArray(opts))}
                      isMulti isClearable closeMenuOnSelect={false}
                      styles={selectStyles} placeholder="All Categories"
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fw-semibold" style={{ fontSize: 14, color: '#475569' }}>
                      <i className="bi bi-calendar3 me-1"></i>Start Date
                    </Form.Label>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      maxDate={new Date()}
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fw-semibold" style={{ fontSize: 14, color: '#475569' }}>
                      <i className="bi bi-calendar3 me-1"></i>End Date
                    </Form.Label>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => handleEndDateChange(date)}
                      dateFormat="dd-MM-yyyy"
                      minDate={startDate}
                      maxDate={new Date()}
                      className="form-control"
                    />
                    {error && <div className="text-danger" style={{ fontSize: 11, marginTop: 4 }}>{error}</div>}
                  </Col>
                </Row>
              </div>

              {/* ── Filter Row 2 — Addon filters + actions ── */}
              <div className="asr-filter-box mb-4">
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Form.Label className="fw-semibold" style={{ fontSize: 14, color: '#475569' }}>
                      <i className="bi bi-box me-1"></i>Addon Group
                    </Form.Label>
                    <Select
                      options={addons.map(a => ({ value: a._id, label: a.title }))}
                      value={addonGroup}
                      onChange={setAddonGroup}
                      styles={selectStyles}
                      isClearable
                      placeholder="All Addon Groups"
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="fw-semibold" style={{ fontSize: 14, color: '#475569' }}>
                      <i className="bi bi-collection me-1"></i>Addon Value
                    </Form.Label>
                    <Select
                      components={animatedComponents}
                      options={filteredAddonValues}
                      value={selectedAddonValue}
                      onChange={setSelectedAddonValue}
                      styles={selectStyles}
                      isClearable
                      placeholder="All Values"
                      isDisabled={!addonGroup}
                    />
                  </Col>

                  <Col md={3}>
                    <Button
                      variant="primary" size="sm" type="submit"
                      className="w-100 fw-semibold"
                      disabled={!startDate || !endDate || selectedOutlets.length === 0 || btnLoading}
                      style={{ borderRadius: 4, height: 35 }}
                    >
                      {btnLoading
                        ? <><Spinner size="sm" animation="border" className="me-2" />Loading…</>
                        : (isMultiSelected || isLongRange(startDate, endDate))
                          ? <><FileEarmarkSpreadsheet className="me-2" size={14} />Schedule Download</>
                          : <><i className="bi bi-bar-chart-line me-2"></i>Generate Report</>}
                    </Button>
                    {(isMultiSelected || isLongRange(startDate, endDate)) && (
                      <span style={{ fontSize: 11, color: '#d97706', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
                        <ExclamationTriangle size={11} />
                        {isMultiSelected
                          ? `${selectedOutlets.length} outlets — will queue as Excel download`
                          : 'Date range >90 days — will queue as Excel download'}
                      </span>
                    )}
                  </Col>

                  {!isMultiSelected && !isLongRange(startDate, endDate) && (
                    <Col md={3}>
                      <DropdownButton
                        variant="outline-success" className="fw-semibold w-100"
                        title={<>{downloadBtnLoading ? <Spinner size="sm" className="me-2" /> : <FileEarmarkSpreadsheet className="me-2" />}Download Report</>}
                        disabled={!hasAnyData || downloadBtnLoading}
                      >
                        <Dropdown.Item onClick={() => handleDownloadExcel(false)} disabled={downloadBtnLoading}>
                          <FileEarmarkSpreadsheet className="me-2 text-success" />Download without costing
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleDownloadExcel(true)} disabled={downloadBtnLoading}>
                          <FileEarmarkSpreadsheet className="me-2 text-success" />Download with costing
                        </Dropdown.Item>
                      </DropdownButton>
                    </Col>
                  )}

                  {!isMultiSelected && !isLongRange(startDate, endDate) && (
                    <Col md={4}>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder="Search product, addon or value…"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ borderRadius: '4px 0 0 4px', fontSize: 13 }}
                        />
                        <Button variant="primary" style={{ zIndex: 0, borderRadius: '0 4px 4px 0' }} disabled={!searchText.trim()}>
                          <Search size={13} className="me-1" />Search
                        </Button>
                      </InputGroup>
                    </Col>
                  )}

                  {/* Stat chips + view toggle */}
                  {!isMultiSelected && hasAnyData && (
                    <>
                      <Col xs={12} className="d-flex gap-2 flex-wrap pt-1">
                        <StatChips />
                      </Col>
                      <Col md="auto" className="ms-auto">
                        <div className="d-flex gap-2">
                          <button type="button" className={`asr-view-btn ${viewMode === VIEW_CUMULATIVE ? 'active' : ''}`} onClick={() => setViewMode(VIEW_CUMULATIVE)}>
                            <i className="bi bi-table me-2"></i>Combined
                          </button>
                          <button type="button" className={`asr-view-btn ${viewMode === VIEW_CATEGORY ? 'active' : ''}`} onClick={() => setViewMode(VIEW_CATEGORY)}>
                            <i className="bi bi-grid-3x3-gap me-2"></i>Category Wise
                          </button>
                        </div>
                      </Col>
                    </>
                  )}
                </Row>
              </div>
            </Form>

            {/* ── Multi-outlet notice ── */}
            {clicked && isMultiOutlet && (
              <div className="asr-multi-notice">
                <span style={{ fontSize: 18, flexShrink: 0 }}>📥</span>
                <div className="asr-multi-notice-text">
                  <strong>Download queued for {selectedOutlets.length} outlets.</strong>{' '}
                  Your report is being generated in the background. Click the{' '}
                  <strong>Downloads</strong> button in the header to track progress and
                  download once ready. You can navigate freely.
                </div>
              </div>
            )}

            {/* ── Empty / Loading / Data states ── */}
            {!clicked ? (
              <div className="d-flex flex-column justify-content-center align-items-center py-5 my-3 rounded-3"
                style={{ background: '#f8fafc', border: '2px dashed #e2e8f0', minHeight: 240 }}>
                <div style={{ width: 64, height: 64, borderRadius: 16, background: '#e7f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <i className="bi bi-sliders text-primary fs-3"></i>
                </div>
                <h6 className="text-muted mb-1">No data to display</h6>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>Select filters above and click <strong>Generate Report</strong></p>
              </div>

            ) : loading && !isMultiOutlet ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 mb-0 text-muted" style={{ fontSize: 13 }}>Loading addon data…</p>
              </div>

            ) : isMultiOutlet ? (
              !loading && (
                <div className="d-flex flex-column justify-content-center align-items-center py-5 my-3 rounded-3"
                  style={{ background: '#f8fafc', border: '2px dashed #e2e8f0', minHeight: 200 }}>
                  <div style={{ width: 64, height: 64, borderRadius: 16, background: '#e7f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <CloudDownload size={28} className="text-primary" />
                  </div>
                  <h6 className="text-muted mb-1">Report queued for download</h6>
                  <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                    Check the <strong>Downloads</strong> panel for your Excel file.
                  </p>
                </div>
              )

            ) : !hasAnyData ? (
              <div className="text-center py-5" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12 }}>
                <i className="bi bi-inbox fs-3 text-muted d-block mb-2"></i>
                <span className="text-muted" style={{ fontSize: 13 }}>No addon data found for the selected criteria</span>
              </div>

            ) : viewMode === VIEW_CUMULATIVE ? (
              /* ══ COMBINED VIEW ══ */
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <Table className="mb-0 asr-table" style={{ fontSize: 13 }}>
                  <TableHead />
                  <tbody>
                    {buildGroupedRows(sortRows(filteredData), true)}
                    <TotalsRow label="Grand Total" totals={grandTotals} withOutlet={true} bgColor="#1e3a5f" />
                  </tbody>
                </Table>
              </div>

            ) : viewMode === VIEW_CATEGORY ? (
              /* ══ CATEGORY WISE VIEW ══ */
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                {/* Expand / Collapse controls */}
                <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                    {filteredCategoryGroups.length} categor{filteredCategoryGroups.length !== 1 ? 'ies' : 'y'}
                  </span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <button type="button" onClick={() => {
                      const keys = {};
                      filteredCategoryGroups.forEach(([k]) => { keys[k] = false; });
                      setCategoryCollapsed(keys);
                    }} style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', color: '#0d6efd', cursor: 'pointer', fontWeight: 600 }}>
                      <i className="bi bi-arrows-expand me-1"></i>Expand All
                    </button>
                    <button type="button" onClick={() => {
                      const keys = {};
                      filteredCategoryGroups.forEach(([k]) => { keys[k] = true; });
                      setCategoryCollapsed(keys);
                    }} style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>
                      <i className="bi bi-arrows-collapse me-1"></i>Collapse All
                    </button>
                  </div>
                </div>
                <Table className="mb-0 asr-table" style={{ fontSize: 13 }}>
                  <TableHead />
                  <tbody>
                    {filteredCategoryGroups.map(([catName, rows]) => {
                      const isCollapsed = categoryCollapsed[catName];
                      return (
                        <React.Fragment key={catName}>
                          <CategoryHeaderRow
                            catName={catName}
                            rows={rows}
                            collapseKey={catName}
                            isCollapsed={isCollapsed}
                            showOutlet={true}
                          />
                          {!isCollapsed && buildGroupedRows(sortRows(rows), true)}
                        </React.Fragment>
                      );
                    })}
                    <TotalsRow label="Grand Total" totals={grandTotals} withOutlet={true} bgColor="#2a4a7f" />
                  </tbody>
                </Table>
              </div>

            ) : (
              /* ══ OUTLET WISE VIEW — TABBED ══ */
              <div>
                <div className="asr-tabs-bar">
                  {filteredOutletGroups.map((og, idx) => {
                    const t = calcTotals(og.rows);
                    return (
                      <button
                        key={og.outletId || og.outletName}
                        type="button"
                        className={`asr-tab-btn ${activeOutletTab === idx ? 'active' : ''}`}
                        onClick={() => setActiveOutletTab(idx)}
                      >
                        <i className="bi bi-shop" style={{ fontSize: 11 }}></i>
                        {og.outletName}
                        <span className="asr-tab-badge">{t.count}</span>
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  const og = filteredOutletGroups[activeOutletTab];
                  if (!og) return null;
                  const t = calcTotals(og.rows);
                  return (
                    <div className="asr-tab-panel">
                      {/* Outlet header card */}
                      <div className="asr-outlet-card-header">
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#0d6efd22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className="bi bi-shop" style={{ color: '#0d6efd', fontSize: 15 }}></i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, color: '#0d6efd', fontSize: 14 }}>{og.outletName}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{og.rows.length} addon value row{og.rows.length !== 1 ? 's' : ''}</div>
                        </div>
                        <div className="d-flex gap-3 flex-wrap">
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: '#0d6efd', lineHeight: 1 }}>{t.count}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}>Total Sales</div>
                          </div>
                          <div style={{ width: 1, background: '#dbeafe' }} />
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 14, fontWeight: 800, color: '#16a34a', lineHeight: 1 }}>{fmt(t.taxable)}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}>Revenue</div>
                          </div>
                        </div>
                      </div>
                      <Table className="mb-0 asr-table" style={{ fontSize: 13 }}>
                        <TableHeadNoOutlet />
                        <tbody>
                          {buildGroupedRows(sortRows(og.rows), false)}
                          <TotalsRow label="Outlet Total" totals={t} withOutlet={false} bgColor="#1e3a5f" />
                        </tbody>
                      </Table>
                    </div>
                  );
                })()}
                {/* Grand total across all outlet tabs */}
                {filteredOutletGroups.length > 1 && (
                  <div style={{ marginTop: 12, borderRadius: 10, overflow: 'hidden', border: '1px solid #1e3a5f' }}>
                    <Table className="mb-0" style={{ fontSize: 13 }}>
                      <tbody>
                        <TotalsRow label="Grand Total — All Outlets" totals={grandTotals} withOutlet={false} bgColor="#2a4a7f" />
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════
          FLOATING DOWNLOAD MANAGER PANEL
      ════════════════════════════════════════════════ */}
      {dmOpen && (
        <div className="ar-dm-panel" ref={dmPanelRef}>
          {/* Header */}
          <div className="ar-dm-header">
            <div className="ar-dm-header-left">
              <div className="ar-dm-header-title">
                <CloudDownload size={17} /> Download Manager
              </div>
              <div className="ar-dm-header-sub">Scheduled Excel export jobs</div>

              {/* DM date filters */}
              <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 9.5, fontWeight: 600, color: 'rgba(255,255,255,.55)', textTransform: 'uppercase', letterSpacing: '.6px' }}>From</span>
                  <DatePicker
                    selected={dmStartDate}
                    onChange={(date) => handleDmDateChange('start', date)}
                    dateFormat="dd-MM-yyyy"
                    maxDate={new Date()}
                    customInput={
                      <input style={{ height: 30, padding: '0 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,.2)', background: 'rgba(255,255,255,.1)', color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer', outline: 'none', width: 112 }} />
                    }
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 9.5, fontWeight: 600, color: 'rgba(255,255,255,.55)', textTransform: 'uppercase', letterSpacing: '.6px' }}>To</span>
                  <DatePicker
                    selected={dmEndDate}
                    onChange={(date) => handleDmDateChange('end', date)}
                    dateFormat="dd-MM-yyyy"
                    minDate={dmStartDate}
                    maxDate={(() => {
                      if (!dmStartDate) return new Date();
                      const max = new Date(dmStartDate); max.setDate(max.getDate() + 30);
                      const today = new Date();
                      return max > today ? today : max;
                    })()}
                    customInput={
                      <input style={{ height: 30, padding: '0 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,.2)', background: 'rgba(255,255,255,.1)', color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer', outline: 'none', width: 112 }} />
                    }
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 9.5, color: 'transparent' }}>–</span>
                  <button
                    style={{ height: 30, padding: '0 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,.25)', background: 'rgba(255,255,255,.15)', color: '#fff', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                    onClick={() => fetchDownloadStatus(false)} disabled={jobsLoading}
                  >
                    <Search size={11} /> Apply
                  </button>
                </div>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', alignSelf: 'flex-end', marginBottom: 3 }}>Max 30-day range</span>
              </div>

              <div className="ar-dm-stats">
                {[
                  { val: downloadJobs.length,                                                   lbl: 'Total'       },
                  { val: pendingCount,                                                           lbl: 'In Progress' },
                  { val: completedCount,                                                         lbl: 'Ready'       },
                  { val: downloadJobs.filter(j => getJobCls(j.status) === 'failed').length,     lbl: 'Failed'      },
                ].map(({ val, lbl }) => (
                  <div key={lbl} className="ar-dm-stat">
                    <div className="ar-dm-stat-val">{val}</div>
                    <div className="ar-dm-stat-lbl">{lbl}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="ar-dm-header-actions">
              {pollingActive && (
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.65)', display: 'flex', alignItems: 'center', gap: 4, marginRight: 4 }}>
                  <span className="ar-spin"><ArrowClockwise size={11} /></span> Live
                </span>
              )}
              <button className="ar-dm-icon-btn" onClick={() => fetchDownloadStatus()} disabled={jobsLoading} title="Refresh">
                {jobsLoading
                  ? <Spinner size="sm" animation="border" style={{ width: 12, height: 12 }} />
                  : <ArrowClockwise size={13} />}
              </button>
              <button className="ar-dm-icon-btn" onClick={() => setDmOpen(false)} title="Close">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Job list */}
          <div className="ar-dm-body">
            {downloadJobs.length === 0 ? (
              <div className="ar-dm-empty">
                <CloudDownload size={40} style={{ opacity: 0.2 }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>No download jobs yet</div>
                <div style={{ fontSize: 12.5, color: '#9ca3af', maxWidth: 280 }}>
                  Select multiple outlets and click <strong>Schedule Download</strong> to queue a job.
                </div>
              </div>
            ) : (
              downloadJobs.map((job, idx) => {
                const cls = getJobCls(job.status);
                const qp  = job.queryParams || {};
                const outlets = job.requestBody?.outletIds || [];
                return (
                  <div key={job._id || idx} className="ar-dm-job">
                    <div className={`ar-dm-job-icon ${cls}`}>{getJobIcon(job.status)}</div>
                    <div className="ar-dm-job-info">
                      <div className="ar-dm-job-name">
                        Addon Sales Report — {qp.startDate || '?'} → {qp.endDate || '?'}
                      </div>
                      <div className="ar-dm-job-meta">
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Clock size={10} />
                          {new Date(job.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {' '}
                          {new Date(job.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                        </span>
                        <span>·</span>
                        <span>{outlets.length} outlet{outlets.length !== 1 ? 's' : ''}</span>
                        {job.errorMessage && <span style={{ color: 'var(--ar-danger)' }}>· {job.errorMessage}</span>}
                      </div>
                      <div className="ar-pb-wrap">
                        <div className={`ar-pb ${cls}`} />
                      </div>
                    </div>
                    <span className={`ar-status-pill ${cls}`}>{getJobLabel(job.status)}</span>
                    {cls === 'completed' && job.storagePath && (
                      <button className="ar-dl-btn" onClick={() => downloadFile(job)}>
                        <BoxArrowDown size={12} /> Download
                      </button>
                    )}
                    {cls === 'completed' && (
                      <button
                        className="ar-regen-btn" title="Regenerate this report"
                        disabled={regeneratingJobId === job._id}
                        onClick={() => handleRegenerateReport(job._id)}
                      >
                        {regeneratingJobId === job._id
                          ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                          : <ArrowClockwise size={14} />}
                      </button>
                    )}
                    <button
                      className="ar-del-btn" title="Cancel this request"
                      disabled={deletingJobId === job._id}
                      onClick={() => handleDeleteReportRequest(job._id)}
                    >
                      {deletingJobId === job._id
                        ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                        : <XCircle size={14} />}
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="ar-dm-footer">
            <span className="ar-dm-footer-info">
              {pollingActive ? (
                <><span className="ar-spin" style={{ display: 'inline-flex' }}><ArrowClockwise size={12} /></span> Auto-refreshing every 5s</>
              ) : ''}
            </span>
            <button
              style={{ height: 30, padding: '0 14px', borderRadius: 6, border: '1.5px solid #e2e6ea', background: '#fff', fontSize: 12, fontWeight: 500, color: 'var(--ar-muted)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, opacity: pollingActive ? 0.5 : 1 }}
              onClick={startPolling} disabled={pollingActive}
            >
              <ArrowClockwise size={11} /> Refresh
            </button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default AddonSalesReport;